// src/components/ConnectionDetailModal.tsx
import React, {useEffect, useState, useMemo} from 'react';
import {ArrowLeftRight, Link, TrendingUp} from 'lucide-react';
import {useSimulationStore} from '../../store/useSimulationStore';
import {
   LineChart, Line, XAxis, YAxis, CartesianGrid,
   Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';
import DetailModal, {KpiItem, ModalSection} from '../common/DetailModal';
import {
   ChartModeToggle,
   NormalizedInfo,
   useChartNormalization,
   ChartMode,
   useChartRange,
   ChartRangeSlider
} from '../common/ChartModeToggle';
import {authHeaders} from '../../store/useAuthStore';
import {apiUrl} from '../../utils/api';

// ─── Metric config (for toggleable chart lines) ──────────────────────────────
const CONN_METRICS = {
   resources_a: {label: 'Node A Resources', color: '#34d399'},
   resources_b: {label: 'Node B Resources', color: '#60a5fa'},
   traffic: {label: 'Traffic', color: '#a78bfa'},
} as const;

type ConnMetricKey = keyof typeof CONN_METRICS;

const ConnectionDetailModal: React.FC = () => {
   const selectedConnectionId = useSimulationStore(state => state.selectedConnectionId);
   const setSelectedConnectionId = useSimulationStore(state => state.setSelectedConnectionId);
   const nodes = useSimulationStore(state => state.nodes);
   
   const connection = useSimulationStore(
      state => state.connections.find(c => c.id === selectedConnectionId)
   );
   
   const [history, setHistory] = useState<any[]>([]);
   const [isLoadingHistory, setIsLoadingHistory] = useState(false);
   const [visibleMetrics, setVisibleMetrics] = useState<Record<ConnMetricKey, boolean>>({
      resources_a: true, resources_b: true, traffic: true,
   });
   const [chartMode, setChartMode] = useState<ChartMode>('absolute');
   
   const toggleMetric = (key: ConnMetricKey) =>
      setVisibleMetrics(prev => ({...prev, [key]: !prev[key]}));
   
   useEffect(() => {
      if (!selectedConnectionId) return;
      
      const fetchHistory = () => {
         setIsLoadingHistory(true);
         fetch(apiUrl(`/api/connections/${selectedConnectionId}/history`), {headers: authHeaders()})
            .then(res => res.json())
            .then(data => {
               setHistory(data);
               setIsLoadingHistory(false);
            })
            .catch(() => setIsLoadingHistory(false));
      };
      
      fetchHistory();
      const interval = setInterval(fetchHistory, 25000);
      return () => clearInterval(interval);
   }, [selectedConnectionId]);
   
   const chartData = useMemo(() => {
      return history.map(h => ({
         tick: h.tick,
         traffic: Math.round((h.node_a_state.traffic_volume + h.node_b_state.traffic_volume) / 2),
         resources_a: Math.round(h.node_a_state.resources),
         resources_b: Math.round(h.node_b_state.resources),
         timestamp: new Date(h.created_at).toLocaleTimeString(),
      }));
   }, [history]);
   
   const {range, setRange, sliced: rangedData} = useChartRange(chartData);
   const metricKeys = useMemo(() => Object.keys(CONN_METRICS), []);
   const {normalizedData} = useChartNormalization(rangedData, metricKeys);
   const displayData = chartMode === 'normalized' ? normalizedData : rangedData;
   
   if (!selectedConnectionId || !connection) return null;
   
   const nodeA = nodes.find(n => n.id === connection.node_a_id);
   const nodeB = nodes.find(n => n.id === connection.node_b_id);
   
   const isTrading = (Number(connection.last_trade_volume) || 0) > 0;
   const pricePerUnit = isTrading
      ? (Number(connection.last_trade_value) || 0) / Math.max(Number(connection.last_trade_volume) || 1, 1)
      : 0;
   
   const levelOf = (s?: string) => s === 'rich' ? 2 : s === 'middle' ? 1 : 0;
   const buyer = levelOf(nodeA?.income_status) >= levelOf(nodeB?.income_status) ? nodeA : nodeB;
   const seller = buyer === nodeA ? nodeB : nodeA;
   
   const buyerResPerCap = buyer
      ? (buyer.resources || 0) / Math.max(buyer.population || 1, 1)
      : 0;
   const desperation = buyerResPerCap < 30 ? 'Critical' : buyerResPerCap < 80 ? 'Low' : buyerResPerCap < 150 ? 'Moderate' : 'Normal';
   const desperationColor = buyerResPerCap < 30 ? 'text-red-400' : buyerResPerCap < 80 ? 'text-orange-400' : buyerResPerCap < 150 ? 'text-yellow-400' : 'text-green-400';
   
   // ─── KPI cards (toggleable) ────────────────────────────────────────────────
   const kpis: KpiItem[] = [
      {
         label: nodeA?.name ?? 'Node A',
         value: (nodeA?.resources || 0).toLocaleString(undefined, {maximumFractionDigits: 0}),
         sub: 'resources',
         color: CONN_METRICS.resources_a.color,
         active: visibleMetrics.resources_a,
         onClick: () => toggleMetric('resources_a'),
      },
      {
         label: nodeB?.name ?? 'Node B',
         value: (nodeB?.resources || 0).toLocaleString(undefined, {maximumFractionDigits: 0}),
         sub: 'resources',
         color: CONN_METRICS.resources_b.color,
         active: visibleMetrics.resources_b,
         onClick: () => toggleMetric('resources_b'),
      },
      {
         label: 'Traffic',
         value: Number(connection.traffic_volume).toLocaleString(),
         sub: 'units/tick',
         color: CONN_METRICS.traffic.color,
         active: visibleMetrics.traffic,
         onClick: () => toggleMetric('traffic'),
      },
      {
         label: 'Maintenance',
         value: `-${Number(connection.maintenance_cost || 0).toFixed(0)}`,
         sub: 'per node/tick',
         color: '#f87171',   // red-400
      },
      {
         label: 'Trade Volume',
         value: isTrading ? (Number(connection.last_trade_volume) || 0).toFixed(1) : '—',
         sub: 'resources/tick',
         color: isTrading ? '#c084fc' : '#6b7280',   // purple-400 or gray-500
      },
   ];
   
   const headerBg = isTrading ? 'bg-purple-900/20' : 'bg-blue-900/20';
   const headerBorder = isTrading ? 'border-purple-800/50' : 'border-blue-800/50';
   
   return (
      <DetailModal
         onClose={() => setSelectedConnectionId(null)}
         maxWidth="max-w-3xl"
         header={{
            icon: isTrading
               ? <ArrowLeftRight className="w-6 h-6 md:w-8 md:h-8" />
               : <Link className="w-6 h-6 md:w-8 md:h-8" />,
            typeLabel: isTrading ? 'Active Trade Connection' : 'Connection',
            title: `${nodeA?.name ?? `Node ${connection.node_a_id}`} ↔ ${nodeB?.name ?? `Node ${connection.node_b_id}`}`,
            subtitle: (
               <div className="flex items-center gap-2 mt-1">
                  <span className="text-gray-500 text-xs font-mono">#{connection.id}</span>
                  <span className="text-gray-500 text-xs">·</span>
                  {isTrading
                     ? <span className="text-xs text-purple-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse inline-block" />
                      Active Trade
                    </span>
                     : <span className="text-xs text-blue-400">No Trade</span>
                  }
               </div>
            ),
            bgClass: headerBg,
            borderClass: headerBorder,
            dotColor: isTrading ? 'bg-purple-400' : 'bg-blue-500',
            dotPulse: isTrading,
         }}
         kpis={kpis}
         kpiHint=""
      >
         {/* ── History Chart ── */}
         <ModalSection
            title="Node Resource History"
            icon={<TrendingUp className="w-4 h-4" />}
            titleExtra={
               <div className="flex items-center gap-2">
                  {isLoadingHistory && (
                     <span className="text-xs text-gray-500 animate-pulse">refreshing…</span>
                  )}
                  <span className="text-xs text-gray-500">
                {chartData.length} snapshots
                <ChartRangeSlider range={range} onChange={setRange} totalPoints={chartData.length} />
              </span>
                  <ChartModeToggle mode={chartMode} onModeChange={setChartMode} />
               </div>
            }
         >
            <NormalizedInfo show={chartMode === 'normalized'} />
            {isLoadingHistory && chartData.length === 0 ? (
               <div className="h-40 flex items-center justify-center text-gray-500 text-sm">Loading history...</div>
            ) : chartData.length === 0 ? (
               <div className="h-40 flex items-center justify-center text-gray-500 text-sm">No history yet — snapshots
                                                                                            save every 5 ticks.</div>
            ) : (
               <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={displayData} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                     <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                     <XAxis
                        dataKey="tick"
                        tick={{fill: '#6b7280', fontSize: 11}}
                        axisLine={false}
                        tickLine={false}
                        label={{value: 'Tick', position: 'insideBottomRight', fill: '#4b5563', fontSize: 10}}
                     />
                     {chartMode === 'absolute' ? (
                        <YAxis
                           tick={{fill: '#6b7280', fontSize: 10}}
                           axisLine={false}
                           tickLine={false}
                        />
                     ) : (
                        <YAxis
                           domain={[0, 100]}
                           tick={{fill: '#6b7280', fontSize: 10}}
                           axisLine={false}
                           tickLine={false}
                           tickFormatter={v => `${v}%`}
                           width={36}
                        />
                     )}
                     {chartMode === 'normalized' && [25, 50, 75].map(y => (
                        <ReferenceLine key={y} y={y} stroke="#374151" strokeDasharray="3 3" strokeOpacity={0.5} />
                     ))}
                     <Tooltip
                        contentStyle={{backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px'}}
                        labelStyle={{color: '#e5e7eb'}}
                        formatter={((value: any, name: any, props: any) => {
                           if (
                              chartMode === 'normalized' &&
                              props?.payload?.tick !== undefined &&
                              typeof name === 'string'
                           ) {
                              const rawPoint = chartData.find((d: any) => d.tick === props.payload.tick);
                              if (rawPoint) {
                                 return [`${value}% (raw: ${rawPoint?.[name as keyof typeof rawPoint] ?? 0})`, name];
                              }
                           }
                           return [value, String(name)];
                        }) as any}
                     
                     />
                     <Legend wrapperStyle={{paddingTop: 8, fontSize: '11px'}} />
                     <Line
                        type="monotone" dataKey="resources_a"
                        name={nodeA?.name ?? 'Node A'}
                        stroke={CONN_METRICS.resources_a.color}
                        strokeWidth={visibleMetrics.resources_a ? 2 : 0}
                        dot={false}
                        hide={!visibleMetrics.resources_a}
                        isAnimationActive={false}
                     />
                     <Line
                        type="monotone" dataKey="resources_b"
                        name={nodeB?.name ?? 'Node B'}
                        stroke={CONN_METRICS.resources_b.color}
                        strokeWidth={visibleMetrics.resources_b ? 2 : 0}
                        dot={false}
                        hide={!visibleMetrics.resources_b}
                        isAnimationActive={false}
                     />
                     <Line
                        type="monotone" dataKey="traffic"
                        name="Traffic"
                        stroke={CONN_METRICS.traffic.color}
                        strokeWidth={visibleMetrics.traffic ? 1 : 0}
                        strokeDasharray="4 2"
                        dot={false}
                        hide={!visibleMetrics.traffic}
                        isAnimationActive={false}
                     />
                  </LineChart>
               </ResponsiveContainer>
            )}
         </ModalSection>
         
         {/* ── Trade Details (only when active) ── */}
         {isTrading && seller && buyer && (
            <ModalSection title="Trade Details" icon={<ArrowLeftRight className="w-4 h-4" />} border="border-purple-800/40">
               <div className="grid grid-cols-2 gap-3">
                  <div>
                     <div className="text-xs text-gray-400 mb-1">Seller (supplier)</div>
                     <div className="text-white font-medium">{seller.name}</div>
                     <div className={`text-xs mt-0.5 ${seller.income_status === 'rich' ? 'text-yellow-400' : seller.income_status === 'middle' ? 'text-blue-400' : 'text-gray-400'}`}>
                        {seller.income_status}
                     </div>
                  </div>
                  <div>
                     <div className="text-xs text-gray-400 mb-1">Buyer (demand)</div>
                     <div className="text-white font-medium">{buyer.name}</div>
                     <div className={`text-xs mt-0.5 ${buyer.income_status === 'rich' ? 'text-yellow-400' : buyer.income_status === 'middle' ? 'text-blue-400' : 'text-gray-400'}`}>
                        {buyer.income_status}
                     </div>
                  </div>
                  <div>
                     <div className="text-xs text-gray-400 mb-1">Price / Unit</div>
                     <div className="text-green-300 font-mono font-semibold">{pricePerUnit.toFixed(2)} credits</div>
                  </div>
                  <div>
                     <div className="text-xs text-gray-400 mb-1">Total Value</div>
                     <div className="text-green-300 font-mono font-semibold">+{(Number(connection.last_trade_value) || 0).toFixed(0)} credits</div>
                  </div>
                  <div>
                     <div className="text-xs text-gray-400 mb-1">Buyer Desperation</div>
                     <div className={`font-semibold text-sm ${desperationColor}`}>{desperation}</div>
                     <div className="text-xs font-mono text-gray-500">{buyerResPerCap.toFixed(0)} res/capita</div>
                  </div>
                  <div>
                     <div className="text-xs text-gray-400 mb-1">Seller Stability Bonus</div>
                     <div className="text-yellow-300 font-mono font-semibold">
                        +{((seller.stability || 0) * 30).toFixed(0)}%
                     </div>
                     <div className="text-xs font-mono text-gray-500">stability {((seller.stability || 0) * 100).toFixed(0)}%</div>
                  </div>
               </div>
            </ModalSection>
         )}
      </DetailModal>
   );
};

export default ConnectionDetailModal;
